import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/utils/api";
import { SystemUserRole, SYSTEM_ROLES } from "@/schemas/systemUserRole";

export interface User {
  id: string;
  username: string;
  email: string;
  role: SystemUserRole;
  isActive: boolean;
  companyId: string;
}

// Helper to convert database role to SystemUserRole
const normalizeRole = (role: string): SystemUserRole => {
  const lowerRole = role.toLowerCase();
  if (lowerRole === "admin") return SYSTEM_ROLES.ADMIN;
  return SYSTEM_ROLES.USER; // default to USER
};

// Fetch all users in the company
export const useUsers = () => {
  return useQuery({
    queryKey: ["users"],
    queryFn: async (): Promise<User[]> => {
      const response = await api.get("/users");
      
      // Transform database roles to SystemUserRole
      const transformedUsers = (response.data || []).map((user: User & { role: string }) => ({
        ...user,
        role: normalizeRole(user.role),
      }));
      
      return transformedUsers;
    },
  });
};

// Update user status (active/inactive)
export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      await api.patch(`/users/${userId}`, { isActive });
      return { userId, isActive };
    },
    onSuccess: ({ userId, isActive }) => {
      // Update the cache immediately for optimistic updates
      queryClient.setQueryData(["users"], (oldData: User[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((user) =>
          user.id === userId ? { ...user, isActive } : user
        );
      });
    },
    onError: () => {
      // Invalidate and refetch on error
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

// Update user role
export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: SystemUserRole }) => {
      await api.patch(`/users/${userId}`, { role });
      return { userId, role };
    },
    onSuccess: ({ userId, role }) => {
      // Update the cache immediately for optimistic updates
      queryClient.setQueryData(["users"], (oldData: User[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((user) =>
          user.id === userId ? { ...user, role } : user
        );
      });
    },
    onError: () => {
      // Invalidate and refetch on error
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};